import type { GlobalDesignSchemeState } from '@visualizer/types';
import { createDesignSchemesState } from '../../designSchemesState';
import { BucketBase, type BucketMigration } from './BucketBase';
import { FacadeComponentType, FacadeDirectionMode, FacadeType, SchemeKey } from '@visualizer/types';
import { addNewField, removeOldField, renameField, replaceField } from './MigrationUtils';

/* VERSIONING GUIDE:
 1) Add update in the specific state (globalState, layerState, DesignSchemeState)
 2) Bump latestVersion when the persisted schema changes.
 3) Add migrateLayersBucketV# function where the change is made
 4) Register it in LAYERS_BUCKET_MIGRATIONS with key N.
EXAMPLE:
 - Add createGlobalState function (gobalState file) enableAutoSave : boolean in  globalState.settings
 - Bump latestVersion in GlobalBucket to 2
 - Add migrateLayersBucketV2 function like this:
    const migrateGlobalBucketV2 = (state: GlobalBucketState): GlobalBucketState => ({
        ...state,
        settings: {
            ...createGlobalState().settings,
            ...state.settings,
            enableAutoSave: state.settings?.enableAutoSave ?? createGlobalState().settings.enableAutoSave,
        },
    }); 
 - Add in LAYERS_BUCKET_MIGRATIONS: 2: migrateGlobalBucketV2
 */

export type DesignSchemesBucketState = GlobalDesignSchemeState;

export class DesignSchemesBucket extends BucketBase<DesignSchemesBucketState> {
    readonly id = 'designSchemes';
    readonly latestVersion = 16;

    protected getMigrations(): Record<number, BucketMigration<DesignSchemesBucketState>> {
        return DESIGN_SCHEMES_BUCKET_MIGRATIONS;
    }

    getDefault(): DesignSchemesBucketState {
        return createDesignSchemesState();
    }

    migrate(persisted: unknown, version = 0): DesignSchemesBucketState {
        const defaults = this.getDefault();
        const persistedState = (persisted as DesignSchemesBucketState | undefined) ?? defaults;

        if (version >= this.latestVersion) {
            return persistedState;
        }

        return this.applyMigrations(persistedState, version);
    }
}

type DesignSchemeSetState = DesignSchemesBucketState['sets'][string];
type DesignSchemeFootprintState = DesignSchemeSetState['footprints'][string];
type PersistedDesignSchemeState = DesignSchemeFootprintState['designSchemes'][string];

// =====================================
// MIGRATION FUNCTIONS
// =====================================

const migrateDesignSchemesBucketV1 = (state: DesignSchemesBucketState): DesignSchemesBucketState => state;

const migrateDesignSchemesBucketV2 = (state: DesignSchemesBucketState): DesignSchemesBucketState => {
    const newState = structuredClone(state);

    const defaults = createDesignSchemesState().defaults;
    const horizontalFuseCoplanar = defaults.floorBased.facadeState.horizontalFuseCoplanar;

    // Add the new field to defaults:
    addNewField(newState, 'defaults.floorBased.facadeState.horizontalFuseCoplanar', horizontalFuseCoplanar);

    // Loop through existing designSchemes and add the new field if necessary:
    for (const [, setState] of Object.entries(newState.sets) as Array<[string, DesignSchemeSetState]>) {
        for (const [, footprintState] of Object.entries(setState.footprints) as Array<[string, DesignSchemeFootprintState]>) {
            for (const [, schemeState] of Object.entries(footprintState.designSchemes) as Array<[string, PersistedDesignSchemeState]>) {
                addNewField(schemeState, 'floorBased.facadeState.horizontalFuseCoplanar', horizontalFuseCoplanar);
            }
        }
    }

    return newState;
};

const migrateDesignSchemesBucketV3 = (state: DesignSchemesBucketState): DesignSchemesBucketState => {
    const defaults = createDesignSchemesState().defaults;
    const newState = structuredClone(state);

    // Replace defaults with the current canonical values.
    replaceField(newState, 'defaults', defaults);

    // Loop through existing designSchemes and add the new fields if necessary.
    for (const [, setState] of Object.entries(newState.sets) as Array<[string, DesignSchemeSetState]>) {
        for (const [, footprintState] of Object.entries(setState.footprints) as Array<[string, DesignSchemeFootprintState]>) {
            for (const [, schemeState] of Object.entries(footprintState.designSchemes) as Array<[string, PersistedDesignSchemeState]>) {
                if (schemeState.schemeKey === SchemeKey.CONVEX_HULL) {
                    addNewField(schemeState, 'convexHull.concave', defaults.convexHull.concave);
                    addNewField(schemeState, 'convexHull.concaveAlpha', defaults.convexHull.concaveAlpha);
                }
                if (schemeState.schemeKey === SchemeKey.ORIENTED_VOLUME) {
                    addNewField(schemeState, 'orientedVolume.concave', defaults.orientedVolume.concave);
                    addNewField(schemeState, 'orientedVolume.concaveAlpha', defaults.orientedVolume.concaveAlpha);
                }
                if (schemeState.schemeKey === SchemeKey.FLOOR_BASED) {
                    addNewField(
                        schemeState,
                        'floorBased.facadeState.verticalCoverageMode',
                        defaults.floorBased.facadeState.verticalCoverageMode
                    );
                    addNewField(
                        schemeState,
                        'floorBased.facadeState.verticalConnectCorners',
                        defaults.floorBased.facadeState.verticalConnectCorners
                    );
                    addNewField(schemeState, 'floorBased.facadeState.verticalClosed', defaults.floorBased.facadeState.verticalClosed);
                }
            }
        }
    }

    return newState;
};

const migrateDesignSchemesBucketV4 = (state: DesignSchemesBucketState): DesignSchemesBucketState => {
    const newState = structuredClone(state);

    for (const [, setState] of Object.entries(newState.sets) as Array<[string, DesignSchemeSetState]>) {
        addNewField(setState, 'closeToDesignSchemes', false);
    }

    return newState;
};

const migrateDesignSchemesBucketV5 = (state: DesignSchemesBucketState): DesignSchemesBucketState => {
    const newState = structuredClone(state) as DesignSchemesBucketState & {
        sets: Record<
            string,
            DesignSchemeSetState & {
                innerSpBlockIdsByLayer?: Record<string, string>;
                outterSpBlockIdsByLayer?: Record<string, string>;
                shadowBlockIdsByLayer?: Record<string, string>;
                keepoutBlockIdsByLayer?: Record<string, string>;
                closeToDesignSchemesBlockIdsByLayer?: Record<string, string>;
            }
        >;
    };

    for (const [, setState] of Object.entries(newState.sets)) {
        delete setState.innerSpBlockIdsByLayer;
        delete setState.outterSpBlockIdsByLayer;
        delete setState.shadowBlockIdsByLayer;
        delete setState.keepoutBlockIdsByLayer;
        delete setState.closeToDesignSchemesBlockIdsByLayer;
    }

    return newState;
};

const migrateDesignSchemesBucketV6 = (state: DesignSchemesBucketState): DesignSchemesBucketState => {
    const defaults = createDesignSchemesState().defaults;
    const newState = structuredClone(state) as DesignSchemesBucketState;

    // Update defaults:
    addNewField(newState, 'defaults.floorBased.facadeState.directionMode', defaults.floorBased.facadeState.directionMode);
    removeOldField(newState, 'defaults.floorBased.facadeState.avgByFloor');

    // Update existing design schemes:
    for (const [, setState] of Object.entries(newState.sets) as Array<[string, DesignSchemeSetState]>) {
        for (const [, footprintState] of Object.entries(setState.footprints) as Array<[string, DesignSchemeFootprintState]>) {
            for (const [, schemeState] of Object.entries(footprintState.designSchemes) as Array<[string, PersistedDesignSchemeState]>) {
                renameField(schemeState, 'floorBased.facadeState.avgByFloor', 'floorBased.facadeState.directionMode', (value) =>
                    value ? FacadeDirectionMode.FLOOR_AVERAGE : FacadeDirectionMode.VOLUME_AVERAGE
                );
                addNewField(schemeState, 'floorBased.facadeState.directionMode', defaults.floorBased.facadeState.directionMode);
            }
        }
    }

    return newState;
};

const migrateDesignSchemesBucketV7 = (state: DesignSchemesBucketState): DesignSchemesBucketState => {
    const defaults = createDesignSchemesState().defaults;
    const newState = structuredClone(state) as DesignSchemesBucketState;

    addNewField(newState, 'defaults.box', defaults.box);

    for (const [, setState] of Object.entries(newState.sets) as Array<[string, DesignSchemeSetState]>) {
        for (const [, footprintState] of Object.entries(setState.footprints) as Array<[string, DesignSchemeFootprintState]>) {
            for (const [, schemeState] of Object.entries(footprintState.designSchemes) as Array<[string, PersistedDesignSchemeState]>) {
                if (schemeState.schemeKey !== 'Box') continue;
                addNewField(schemeState, 'box', defaults.box);
            }
        }
    }

    return newState;
};

const migrateDesignSchemesBucketV8 = (state: DesignSchemesBucketState): DesignSchemesBucketState => {
    const defaults = createDesignSchemesState().defaults;
    const newState = structuredClone(state) as DesignSchemesBucketState;

    addNewField(newState, 'defaults.floorBased.boxAlpha', defaults.floorBased.boxAlpha);

    for (const [, setState] of Object.entries(newState.sets) as Array<[string, DesignSchemeSetState]>) {
        for (const [, footprintState] of Object.entries(setState.footprints) as Array<[string, DesignSchemeFootprintState]>) {
            for (const [, schemeState] of Object.entries(footprintState.designSchemes) as Array<[string, PersistedDesignSchemeState]>) {
                if (schemeState.schemeKey !== SchemeKey.FLOOR_BASED) continue;
                addNewField(schemeState, 'floorBased.boxAlpha', defaults.floorBased.boxAlpha);
            }
        }
    }

    return newState;
};

const migrateDesignSchemesBucketV9 = (state: DesignSchemesBucketState): DesignSchemesBucketState => {
    const defaults = createDesignSchemesState().defaults;
    const newState = structuredClone(state) as DesignSchemesBucketState;

    addNewField(newState, 'defaults.floorBased.facadeState.componentTypes', defaults.floorBased.facadeState.componentTypes);

    for (const [, setState] of Object.entries(newState.sets) as Array<[string, DesignSchemeSetState]>) {
        for (const [, footprintState] of Object.entries(setState.footprints) as Array<[string, DesignSchemeFootprintState]>) {
            for (const [, schemeState] of Object.entries(footprintState.designSchemes) as Array<[string, PersistedDesignSchemeState]>) {
                if (schemeState.schemeKey !== SchemeKey.FLOOR_BASED) continue;

                addNewField(schemeState, 'floorBased.facadeState.componentTypes', defaults.floorBased.facadeState.componentTypes);

                const facadeState = schemeState.floorBased?.facadeState as
                    | {
                          type: FacadeType;
                          componentTypes?: FacadeComponentType[];
                      }
                    | undefined;
                if (!facadeState) continue;

                if (facadeState.type === FacadeType.VERTICALLY_ORIENTED) {
                    facadeState.type = FacadeType.TRANSPARENT;
                    facadeState.componentTypes = [FacadeComponentType.PV_PANELS];
                }
            }
        }
    }

    return newState;
};

const migrateDesignSchemesBucketV10 = (state: DesignSchemesBucketState): DesignSchemesBucketState => {
    const defaults = createDesignSchemesState().defaults;
    const newState = structuredClone(state) as DesignSchemesBucketState;

    addNewField(newState, 'defaults.floorBased.facadeState.greenDepth', defaults.floorBased.facadeState.greenDepth);

    for (const [, setState] of Object.entries(newState.sets) as Array<[string, DesignSchemeSetState]>) {
        for (const [, footprintState] of Object.entries(setState.footprints) as Array<[string, DesignSchemeFootprintState]>) {
            for (const [, schemeState] of Object.entries(footprintState.designSchemes) as Array<[string, PersistedDesignSchemeState]>) {
                if (schemeState.schemeKey !== SchemeKey.FLOOR_BASED) continue;

                addNewField(schemeState, 'floorBased.facadeState.greenDepth', defaults.floorBased.facadeState.greenDepth);
            }
        }
    }

    return newState;
};

const migrateDesignSchemesBucketV11 = (state: DesignSchemesBucketState): DesignSchemesBucketState => {
    const defaults = createDesignSchemesState().defaults;
    const newState = structuredClone(state) as DesignSchemesBucketState;

    addNewField(newState, 'defaults.floorBased.facadeState.balconyLength', defaults.floorBased.facadeState.balconyLength);

    for (const [, setState] of Object.entries(newState.sets) as Array<[string, DesignSchemeSetState]>) {
        for (const [, footprintState] of Object.entries(setState.footprints) as Array<[string, DesignSchemeFootprintState]>) {
            for (const [, schemeState] of Object.entries(footprintState.designSchemes) as Array<[string, PersistedDesignSchemeState]>) {
                if (schemeState.schemeKey !== SchemeKey.FLOOR_BASED) continue;

                addNewField(schemeState, 'floorBased.facadeState.balconyLength', defaults.floorBased.facadeState.balconyLength);
            }
        }
    }

    return newState;
};

const migrateDesignSchemesBucketV12 = (state: DesignSchemesBucketState): DesignSchemesBucketState => {
    const defaults = createDesignSchemesState().defaults;
    const newState = structuredClone(state) as DesignSchemesBucketState;

    addNewField(newState, 'defaults.floorBased.facadeState.componentLink', defaults.floorBased.facadeState.componentLink);

    for (const [, setState] of Object.entries(newState.sets) as Array<[string, DesignSchemeSetState]>) {
        for (const [, footprintState] of Object.entries(setState.footprints) as Array<[string, DesignSchemeFootprintState]>) {
            for (const [, schemeState] of Object.entries(footprintState.designSchemes) as Array<[string, PersistedDesignSchemeState]>) {
                if (schemeState.schemeKey !== SchemeKey.FLOOR_BASED) continue;

                addNewField(schemeState, 'floorBased.facadeState.componentLink', defaults.floorBased.facadeState.componentLink);
            }
        }
    }

    return newState;
};

const migrateDesignSchemesBucketV13 = (state: DesignSchemesBucketState): DesignSchemesBucketState => {
    const defaults = createDesignSchemesState().defaults;
    const newState = structuredClone(state) as DesignSchemesBucketState;

    addNewField(newState, 'defaults.floorBased.facadeState.componentTypes', defaults.floorBased.facadeState.componentTypes);
    addNewField(newState, 'defaults.floorBased.facadeState.componentLayerIds', defaults.floorBased.facadeState.componentLayerIds);
    addNewField(newState, 'defaults.floorBased.facadeState.balconyConnectCorners', defaults.floorBased.facadeState.balconyConnectCorners);

    const normalizeComponentTypes = (facadeState: { componentType?: FacadeComponentType; componentTypes?: FacadeComponentType[] }) => {
        if (Array.isArray(facadeState.componentTypes)) return;
        if (facadeState.componentType === undefined || facadeState.componentType === FacadeComponentType.NOTHING) {
            facadeState.componentTypes = [];
            return;
        }
        facadeState.componentTypes = [facadeState.componentType];
    };

    const defaultFacadeState = newState.defaults.floorBased.facadeState as {
        componentType?: FacadeComponentType;
        componentTypes?: FacadeComponentType[];
    };
    normalizeComponentTypes(defaultFacadeState);
    removeOldField(newState, 'defaults.floorBased.facadeState.componentType');

    for (const [, setState] of Object.entries(newState.sets) as Array<[string, DesignSchemeSetState]>) {
        for (const [, footprintState] of Object.entries(setState.footprints) as Array<[string, DesignSchemeFootprintState]>) {
            for (const [, schemeState] of Object.entries(footprintState.designSchemes) as Array<[string, PersistedDesignSchemeState]>) {
                if (schemeState.schemeKey !== SchemeKey.FLOOR_BASED) continue;

                const facadeState = schemeState.floorBased?.facadeState as
                    | {
                          componentType?: FacadeComponentType;
                          componentTypes?: FacadeComponentType[];
                      }
                    | undefined;
                if (!facadeState) continue;

                normalizeComponentTypes(facadeState);
                removeOldField(schemeState, 'floorBased.facadeState.componentType');
                addNewField(schemeState, 'floorBased.facadeState.componentLayerIds', defaults.floorBased.facadeState.componentLayerIds);
                addNewField(
                    schemeState,
                    'floorBased.facadeState.balconyConnectCorners',
                    defaults.floorBased.facadeState.balconyConnectCorners
                );
            }
        }
    }

    return newState;
};

const migrateDesignSchemesBucketV14 = (state: DesignSchemesBucketState): DesignSchemesBucketState => {
    const defaults = createDesignSchemesState().defaults;
    const newState = structuredClone(state) as DesignSchemesBucketState;

    addNewField(newState, 'defaults.floorBased.linkToMainLayer', defaults.floorBased.linkToMainLayer);

    for (const [, setState] of Object.entries(newState.sets) as Array<[string, DesignSchemeSetState]>) {
        for (const [, footprintState] of Object.entries(setState.footprints) as Array<[string, DesignSchemeFootprintState]>) {
            for (const [, schemeState] of Object.entries(footprintState.designSchemes) as Array<[string, PersistedDesignSchemeState]>) {
                if (schemeState.schemeKey !== SchemeKey.FLOOR_BASED) continue;
                addNewField(schemeState, 'floorBased.linkToMainLayer', defaults.floorBased.linkToMainLayer);
            }
        }
    }

    return newState;
};

const migrateDesignSchemesBucketV15 = (state: DesignSchemesBucketState): DesignSchemesBucketState => {
    const defaults = createDesignSchemesState().defaults;
    const newState = structuredClone(state) as DesignSchemesBucketState;

    addNewField(newState, 'defaults.floorBased.facadeState.shadingDepth', defaults.floorBased.facadeState.shadingDepth);
    addNewField(newState, 'defaults.floorBased.facadeState.shadingConnectCorners', defaults.floorBased.facadeState.shadingConnectCorners);
    addNewField(newState, 'defaults.floorBased.facadeState.shadingManual', defaults.floorBased.facadeState.shadingManual);
    addNewField(
        newState,
        'defaults.floorBased.facadeState.shadingAutomaticRectangle',
        defaults.floorBased.facadeState.shadingAutomaticRectangle
    );
    addNewField(
        newState,
        'defaults.floorBased.facadeState.shadingAutomaticRectangleReference',
        defaults.floorBased.facadeState.shadingAutomaticRectangleReference
    );
    addNewField(
        newState,
        'defaults.floorBased.facadeState.shadingAutomaticLateralVerticalShade',
        defaults.floorBased.facadeState.shadingAutomaticLateralVerticalShade
    );

    for (const [, setState] of Object.entries(newState.sets) as Array<[string, DesignSchemeSetState]>) {
        for (const [, footprintState] of Object.entries(setState.footprints) as Array<[string, DesignSchemeFootprintState]>) {
            for (const [, schemeState] of Object.entries(footprintState.designSchemes) as Array<[string, PersistedDesignSchemeState]>) {
                if (schemeState.schemeKey !== SchemeKey.FLOOR_BASED) continue;
                addNewField(schemeState, 'floorBased.facadeState.shadingDepth', defaults.floorBased.facadeState.shadingDepth);
                addNewField(
                    schemeState,
                    'floorBased.facadeState.shadingConnectCorners',
                    defaults.floorBased.facadeState.shadingConnectCorners
                );
                addNewField(schemeState, 'floorBased.facadeState.shadingManual', defaults.floorBased.facadeState.shadingManual);
                addNewField(
                    schemeState,
                    'floorBased.facadeState.shadingAutomaticRectangle',
                    defaults.floorBased.facadeState.shadingAutomaticRectangle
                );
                addNewField(
                    schemeState,
                    'floorBased.facadeState.shadingAutomaticRectangleReference',
                    defaults.floorBased.facadeState.shadingAutomaticRectangleReference
                );
                addNewField(
                    schemeState,
                    'floorBased.facadeState.shadingAutomaticLateralVerticalShade',
                    defaults.floorBased.facadeState.shadingAutomaticLateralVerticalShade
                );
            }
        }
    }

    return newState;
};

const migrateDesignSchemesBucketV16 = (state: DesignSchemesBucketState): DesignSchemesBucketState => {
    const defaults = createDesignSchemesState().defaults;
    const newState = structuredClone(state) as DesignSchemesBucketState;

    addNewField(newState, 'defaults.floorBased.facadeState.balconyOnlyOnOpenings', defaults.floorBased.facadeState.balconyOnlyOnOpenings);
    addNewField(newState, 'defaults.floorBased.facadeState.componentDirectionMode', defaults.floorBased.facadeState.componentDirectionMode);
    addNewField(newState, 'defaults.floorBased.facadeState.pvOffset', defaults.floorBased.facadeState.pvOffset);
    addNewField(newState, 'defaults.convexHull.transparent', defaults.convexHull.transparent);
    addNewField(newState, 'defaults.orientedVolume.transparent', defaults.orientedVolume.transparent);
    addNewField(newState, 'defaults.box.transparent', defaults.box.transparent);
    addNewField(newState, 'defaults.box.snapToVolume', defaults.box.snapToVolume);

    for (const [, setState] of Object.entries(newState.sets) as Array<[string, DesignSchemeSetState]>) {
        for (const [, footprintState] of Object.entries(setState.footprints) as Array<[string, DesignSchemeFootprintState]>) {
            for (const [, schemeState] of Object.entries(footprintState.designSchemes) as Array<[string, PersistedDesignSchemeState]>) {
                if (schemeState.schemeKey === SchemeKey.FLOOR_BASED) {
                    addNewField(
                        schemeState,
                        'floorBased.facadeState.balconyOnlyOnOpenings',
                        defaults.floorBased.facadeState.balconyOnlyOnOpenings
                    );
                    addNewField(
                        schemeState,
                        'floorBased.facadeState.componentDirectionMode',
                        defaults.floorBased.facadeState.componentDirectionMode
                    );
                    addNewField(schemeState, 'floorBased.facadeState.pvOffset', defaults.floorBased.facadeState.pvOffset);
                } else if (schemeState.schemeKey === SchemeKey.CONVEX_HULL) {
                    addNewField(schemeState, 'convexHull.transparent', defaults.convexHull.transparent);
                } else if (schemeState.schemeKey === SchemeKey.ORIENTED_VOLUME) {
                    addNewField(schemeState, 'orientedVolume.transparent', defaults.orientedVolume.transparent);
                } else if (schemeState.schemeKey === SchemeKey.BOX) {
                    addNewField(schemeState, 'box.transparent', defaults.box.transparent);
                    addNewField(schemeState, 'box.snapToVolume', defaults.box.snapToVolume);
                }
            }
        }
    }

    return newState;
};

const DESIGN_SCHEMES_BUCKET_MIGRATIONS: Record<number, BucketMigration<DesignSchemesBucketState>> = {
    1: migrateDesignSchemesBucketV1,
    2: migrateDesignSchemesBucketV2,
    3: migrateDesignSchemesBucketV3,
    4: migrateDesignSchemesBucketV4,
    5: migrateDesignSchemesBucketV5,
    6: migrateDesignSchemesBucketV6,
    7: migrateDesignSchemesBucketV7,
    8: migrateDesignSchemesBucketV8,
    9: migrateDesignSchemesBucketV9,
    10: migrateDesignSchemesBucketV10,
    11: migrateDesignSchemesBucketV11,
    12: migrateDesignSchemesBucketV12,
    13: migrateDesignSchemesBucketV13,
    14: migrateDesignSchemesBucketV14,
    15: migrateDesignSchemesBucketV15,
    16: migrateDesignSchemesBucketV16,
};
